import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCard } from '../hooks/useCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCardDetailMutations } from '../hooks/useCardDetailMutations';
import { useChecklistMutations } from '../hooks/useChecklistMutations';
import { useLabels } from '../hooks/useLabels';
import { useLabelMutations, useCardLabelMutations } from '../hooks/useLabelMutations';
import { useModal } from '../context/modals/useModal';
import { useInlineEditing } from '../hooks/useInlineEditing';
import { useCardLinks } from '../hooks/useCardLinks';
import { CardSidebar } from '../components/CardSidebar';
import { CardComments } from '../components/CardComments';
import { CardChecklists } from '../components/CardChecklists';
import { CardLabels } from '../components/CardLabels';
import { CardEpics } from '../components/CardEpics';
import { CardAttachments } from '../components/CardAttachments';
import { CardLinks } from '../components/CardLinks';
import { DeleteCardModal } from '../components/DeleteCardModal';
import { CardActionDropdown } from '../components/CardActionDropdown';
import { CreateLinkedCardModal } from '../components/CreateLinkedCardModal';
import { LinkExistingCardModal } from '../components/LinkExistingCardModal';
import ArrowLeftIcon from '../components/icons/ArrowLeftIcon';
import { NarrowPageLayout } from '@/components/NarrowPageLayout';
import { formatDateTime } from '../utils/dateFormat';
import Edit2Icon from '../components/icons/Edit2Icon';

export function CardDetail() {
  const { id: boardId, cardId } = useParams<{ id: string; cardId: string }>();
  const navigate = useNavigate();
  const { card, fetchCard } = useCard(parseInt(cardId || '0'));
  useDocumentTitle(card?.name);
  const {
    updateCardNameAndDescription,
    deleteCardWithConfirmation,
    addComment,
    editComment,
    deleteCommentWithConfirmation,
    createLinkedCardFromModal,
    convertCheckItemToCard,
  } = useCardDetailMutations(parseInt(cardId || '0'), card, fetchCard);

  const { openModal } = useModal();
  const checklistMutations = useChecklistMutations(parseInt(cardId || '0'), fetchCard);

  // Card links
  const cardLinksHook = useCardLinks(parseInt(cardId || '0'));

  const [showCreateLinkedModal, setShowCreateLinkedModal] = useState(false);
  const [showLinkExistingModal, setShowLinkExistingModal] = useState(false);

  // Inline editing hook
  const inlineEditing = useInlineEditing(card, updateCardNameAndDescription);

  // Labels functionality
  const { labels, refetch: refetchLabels } = useLabels(parseInt(boardId || '0'));
  const { addLabel } = useLabelMutations(parseInt(boardId || '0'), refetchLabels);
  const { addLabelToCardMutation, removeLabelFromCardMutation } = useCardLabelMutations(
    parseInt(cardId || '0'),
    fetchCard
  );

  const handleDeleteCard = () => {
    if (!card) return;

    openModal((props) => (
      <DeleteCardModal
        cardName={card.name}
        onDelete={async () => {
          deleteCardWithConfirmation(() => {
            props.onClose();
            navigate(`/boards/${boardId}`);
          });
        }}
        onClose={props.onClose}
      />
    ));
  };

  if (!card) {
    return null;
  }

  return (
    <NarrowPageLayout>
      <div className="flex justify-between items-start">
        <div>
          <Link
            to={`/boards/${boardId}`}
            className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-1"
          >
            <span className="w-4 h-4">
              <ArrowLeftIcon />
            </span>
            Back to Board
          </Link>
          <div className="flex items-center gap-2 mt-2">
            {inlineEditing.isEditingName ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  ref={inlineEditing.nameInputRef}
                  type="text"
                  value={inlineEditing.editedName}
                  onChange={(e) => inlineEditing.setEditedName(e.target.value)}
                  onKeyDown={inlineEditing.handleNameKeyPress}
                  className="flex-1 text-3xl font-bold text-white bg-gray-800 border-2 border-blue-500 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={inlineEditing.handleSaveName}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={inlineEditing.handleCancelEditingName}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-white">{card.name}</h1>
                <button
                  onClick={inlineEditing.handleStartEditingName}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Edit card name"
                >
                  <span className="w-5 h-5">
                    <Edit2Icon />
                  </span>
                </button>
              </div>
            )}
          </div>
          <p className="text-gray-400 text-sm mt-1">
            In list <span className="text-white font-medium">{card.list_name}</span> • Created{' '}
            {formatDateTime(card.created_at)}
          </p>
        </div>
        <CardActionDropdown
          onEdit={inlineEditing.handleStartEditingName}
          onDelete={handleDeleteCard}
          onCreateLinkedCard={() => setShowCreateLinkedModal(true)}
          onLinkExistingCard={() => setShowLinkExistingModal(true)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Description Section */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">Description</h2>
              {!inlineEditing.isEditingDescription && (
                <button
                  onClick={inlineEditing.handleStartEditingDescription}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  Edit
                </button>
              )}
            </div>
            {inlineEditing.isEditingDescription ? (
              <div className="space-y-3">
                <textarea
                  ref={inlineEditing.descriptionInputRef}
                  value={inlineEditing.editedDescription}
                  onChange={(e) => inlineEditing.setEditedDescription(e.target.value)}
                  onKeyDown={inlineEditing.handleDescriptionKeyPress}
                  className="w-full min-h-[200px] text-gray-300 bg-gray-700 border-2 border-blue-500 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  placeholder="Add a description..."
                />
                <div className="flex gap-2">
                  <button
                    onClick={inlineEditing.handleSaveDescription}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={inlineEditing.handleCancelEditingDescription}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-300 whitespace-pre-wrap">
                {card.description || 'No description added yet.'}
              </p>
            )}
          </div>

          {/* Labels Section */}
          <CardLabels
            labels={labels}
            cardLabels={card.labels || []}
            addLabel={addLabel}
            addLabelToCard={addLabelToCardMutation}
            removeLabelFromCard={removeLabelFromCardMutation}
            refetchLabels={refetchLabels}
            refetchCard={fetchCard}
          />

          {/* Epic Section */}
          <CardEpics
            cardEpics={card.epic ? [card.epic] : []}
            boardId={parseInt(boardId || '0')}
            cardId={parseInt(cardId || '0')}
            refetchCard={fetchCard}
          />

          {/* Linked Cards Section */}
          <CardLinks
            links={cardLinksHook.links}
            boardId={parseInt(boardId || '0')}
            onUnlink={cardLinksHook.unlinkCard}
          />

          {/* Checklists Section */}
          <CardChecklists
            checklists={card.checklists || []}
            cardId={parseInt(cardId || '0')}
            addChecklist={checklistMutations.addChecklist}
            removeChecklist={checklistMutations.removeChecklist}
            addCheckItem={checklistMutations.addCheckItem}
            toggleCheckItem={checklistMutations.toggleCheckItem}
            editCheckItem={checklistMutations.editCheckItem}
            removeCheckItem={checklistMutations.removeCheckItem}
            onConvertToCard={(itemName: string, itemId: number) =>
              convertCheckItemToCard(
                itemName,
                itemId,
                card.list_id,
                checklistMutations.removeCheckItem,
                cardLinksHook.fetchLinks
              )
            }
          />

          <CardAttachments cardId={cardId ? parseInt(cardId) : undefined} />

          <CardComments
            card={card}
            addComment={addComment}
            editComment={editComment}
            deleteCommentWithConfirmation={deleteCommentWithConfirmation}
            openModal={openModal}
          />
        </div>

        <CardSidebar card={card} />
      </div>

      {/* Create Linked Card Modal */}
      {showCreateLinkedModal && card && (
        <CreateLinkedCardModal
          parentCardName={card.name}
          onClose={() => setShowCreateLinkedModal(false)}
          onSubmit={async (name: string, description: string) => {
            const success = await createLinkedCardFromModal(
              name,
              description,
              card.list_id,
              cardLinksHook.fetchLinks
            );
            if (success) {
              setShowCreateLinkedModal(false);
            }
          }}
        />
      )}

      {/* Link Existing Card Modal */}
      {showLinkExistingModal && (
        <LinkExistingCardModal
          boardId={parseInt(boardId || '0')}
          currentCardId={parseInt(cardId || '0')}
          onClose={() => setShowLinkExistingModal(false)}
          onLinked={() => cardLinksHook.fetchLinks()}
        />
      )}
    </NarrowPageLayout>
  );
}
